// Reads the play-index <form id="filters"> into a Partial<Filters>.
//
// Mirrors the Query(...) defaulting / parsing the FastAPI route does for
// /events: empty strings → null, comma-separated lists for the prev-
// event-type/zone inputs, mm:ss for clock_{min,max}.

import { applyFilterDefaults, type Filters } from "./filters";

/** "12:34" → 754 seconds. Anything not matching mm:ss returns null. */
function parseClock(s: string | null): number | null {
  if (!s) return null;
  const m = /^([0-9]+)(?::([0-5][0-9]))?$/.exec(s.trim());
  if (!m) return null;
  const minutes = Number(m[1]);
  const seconds = m[2] !== undefined ? Number(m[2]) : 0;
  return minutes * 60 + seconds;
}

function val(form: HTMLFormElement, name: string): string | null {
  const el = form.elements.namedItem(name);
  if (el === null) return null;
  // RadioNodeList → take the first non-empty value.
  if ("length" in el && !(el instanceof HTMLSelectElement)) {
    const list = el as RadioNodeList;
    for (const node of Array.from(list)) {
      if (node instanceof HTMLInputElement && node.value) return node.value;
    }
    return null;
  }
  const v = (el as HTMLInputElement | HTMLSelectElement).value;
  return v === "" ? null : v;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const el = form.elements.namedItem(name);
  if (el === null) return false;
  if (el instanceof HTMLInputElement) return el.checked;
  return false;
}

function intOrNull(s: string | null): number | null {
  if (s === null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function splitList(s: string | null): string[] | null {
  if (!s) return null;
  const parts = s
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return parts.length > 0 ? parts : null;
}

export function readFilters(form: HTMLFormElement): Filters {
  const partial: Partial<Filters> = {
    team_id: intOrNull(val(form, "team_id")),
    player_id: intOrNull(val(form, "player_id")),
    goalie_id: intOrNull(val(form, "goalie_id")),
    event_group: val(form, "event_group") ?? "shots_on_net",
    zone: val(form, "zone"),
    score_state: val(form, "score_state"),
    strength_state: val(form, "strength_state"),
    season_type: val(form, "season_type"),
    on_ice_player_id: intOrNull(val(form, "on_ice_player_id")),
    on_ice_with_player_id: intOrNull(val(form, "on_ice_with_player_id")),
    on_ice_without_player_id: intOrNull(val(form, "on_ice_without_player_id")),
    period: intOrNull(val(form, "period")),
    clock_seconds_min: parseClock(val(form, "clock_min")),
    clock_seconds_max: parseClock(val(form, "clock_max")),
    prev_event_types: splitList(val(form, "prev_event_types")),
    prev_event_team: val(form, "prev_event_team"),
    prev_event_zones: splitList(val(form, "prev_event_zones")),
    time_since_prev_event_max_seconds: intOrNull(
      val(form, "time_since_prev_event_max_seconds"),
    ),
    no_intervening_reset: checked(form, "no_intervening_reset"),
    is_rebound_only: checked(form, "is_rebound_only"),
    pressure_sequence_only: checked(form, "pressure_sequence_only"),
    goalie_state: val(form, "goalie_state"),
    score_bucket: val(form, "score_bucket"),
    period_state: val(form, "period_state"),
    late_game: checked(form, "late_game") || null,
    delayed_penalty: checked(form, "delayed_penalty") || null,
    chain_started_off_faceoff: checked(form, "chain_started_off_faceoff") || null,
    chain_started_off_rush: checked(form, "chain_started_off_rush") || null,
    chain_min_events: intOrNull(val(form, "chain_min_events")),
  };
  return applyFilterDefaults(partial);
}

/** Pre-populate the form from `?key=value` query-string params. Mirrors
 *  the inline script that used to live at the bottom of index.html. */
export function hydrateFromUrl(form: HTMLFormElement): void {
  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    const el = form.elements.namedItem(key);
    if (el === null) return;
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      el.checked = value === "1" || value === "on" || value === "true";
    } else if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
      if (value) el.value = value;
    }
  });
}
